// ONBOARDING-P4: the case's after-go-live tabs. Messages: the client's system address and what
// the HQ dispatcher did with each message to it (class, route, quarantine), read-only — replies
// come from the operator, by hand. 30-day review: what the review collected (KPIs per seat,
// failures, usage cost, open recoveries), the summary the operator may share with the client
// (from the HTVA team; it never names an internal agent) and private notes, then "Reviewed".
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Label,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../ops-parts";
import { LoadError, Loading, Section } from "../screen-parts";
import { day, when } from "./onboarding-labels";

type Dispatch = OperationOutput<"operator.onboarding.dispatch.list">;
type Review = OperationOutput<"operator.onboarding.review.get">;

const CLASS: Record<string, string> = {
  support_question: "Support question",
  change_request: "Change request",
  incident: "Incident",
  noise: "Noise",
};
const ROUTE: Record<string, { label: string; variant: "neutral" | "warning" | "success" }> = {
  operator: { label: "To you", variant: "success" },
  quarantine: { label: "Quarantine", variant: "warning" },
  logged: { label: "Logged", variant: "neutral" },
};
const usd = (micros: number) => `$${(micros / 1_000_000).toFixed(2)}`;

export function MessagesTab({ api, companyId }: { api: Api; companyId: string }) {
  const q = useQuery({
    queryKey: ["operator", "onboarding", "dispatch", companyId],
    queryFn: () =>
      api.client.operator.onboarding.dispatch.list({ params: { company_id: companyId } }),
  });
  if (q.isPending) return <Loading label="Loading the client's messages" />;
  if (q.isError) return <LoadError what="The client's messages" error={q.error} />;
  const v: Dispatch = q.data;
  return (
    <div className="flex flex-col gap-4">
      <p data-slot="system-address">
        {v.address
          ? `System address: ${v.address.address} (open since ${day(v.address.opened_at)}).`
          : "The system address opens at the handover."}
      </p>
      <p className="text-muted-foreground text-sm">
        Replies come from you. Items in quarantine reached no agent: check the sender first.
      </p>
      {v.items.length === 0 ? (
        <p className="text-muted-foreground">No messages yet.</p>
      ) : (
        <Table data-slot="dispatch-items">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Received</TableHead>
              <TableHead scope="col">From</TableHead>
              <TableHead scope="col">Class</TableHead>
              <TableHead scope="col">Route</TableHead>
              <TableHead scope="col">Message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {v.items.map((i) => {
              const r = ROUTE[i.route] ?? { label: i.route, variant: "neutral" as const };
              return (
                <TableRow key={i.id}>
                  <TableCell className="tabular-nums">{when(i.received_at)}</TableCell>
                  <TableCell>
                    {i.sender}
                    {i.sender_verified ? "" : " (unverified)"}
                  </TableCell>
                  <TableCell>{CLASS[i.class] ?? i.class}</TableCell>
                  <TableCell>
                    <Badge variant={r.variant}>{r.label}</Badge>
                    {i.quarantine_reason ? ` ${i.quarantine_reason.replace(/_/g, " ")}` : ""}
                  </TableCell>
                  <TableCell className="max-w-md whitespace-pre-wrap break-words">
                    {i.subject ? <strong>{i.subject}</strong> : null}
                    {i.subject ? "\n" : ""}
                    {i.quoted}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export function ReviewTab({ api, companyId }: { api: Api; companyId: string }) {
  const key = ["operator", "onboarding", "review", companyId];
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.operator.onboarding.review.get({ params: { company_id: companyId } }),
    retry: false,
  });
  const [summary, setSummary] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!q.data) return;
    setSummary(q.data.client_summary ?? "");
    setNote(q.data.operator_note ?? "");
  }, [q.data]);
  const save = useMutation({
    mutationFn: (reviewed: boolean) =>
      api.client.operator.onboarding.review.update({
        params: { company_id: companyId },
        body: { client_summary: summary, operator_note: note, ...(reviewed ? { reviewed } : {}) },
      }),
    onSuccess: (v) => qc.setQueryData(key, v),
  });
  if (q.isPending) return <Loading label="Loading the 30-day review" />;
  if (q.isError)
    return <p className="text-muted-foreground">No review yet: it is scheduled at the handover.</p>;
  const r: Review = q.data;
  if (r.status === "scheduled" || !r.metrics)
    return (
      <p data-slot="review-scheduled">
        The 30-day review is due {day(r.due_at)}: its numbers are collected then.
      </p>
    );
  const m = r.metrics;
  return (
    <div className="flex flex-col gap-4">
      <p>
        Collected {r.collected_at ? when(r.collected_at) : ""} for {day(m.window.from)} to{" "}
        {day(m.window.to)}. {m.failures.runs} run(s), {m.failures.failed} failed; usage{" "}
        {usd(m.usage.cost_micros)} {m.usage.currency}.
      </p>
      <Section id="review-seats" title="KPIs per seat">
        <Table data-slot="review-seats">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Seat</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">KPIs (actual / target)</TableHead>
              <TableHead scope="col">Runs</TableHead>
              <TableHead scope="col">Failed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {m.seats.map((s) => (
              <TableRow key={s.seat_key ?? s.title}>
                <TableCell>{s.title}</TableCell>
                <TableCell>{s.status}</TableCell>
                <TableCell>
                  {s.kpis.length === 0
                    ? "—"
                    : s.kpis
                        .map((k) => `${k.name}: ${k.actual ?? "—"} / ${k.target ?? "—"}`)
                        .join("; ")}
                </TableCell>
                <TableCell className="tabular-nums">{s.runs}</TableCell>
                <TableCell className="tabular-nums">{s.failed}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section id="review-recoveries" title="Open recoveries">
        {m.open_recoveries.length === 0 ? (
          <p className="text-muted-foreground">Every seat is ready.</p>
        ) : (
          <ul className="list-disc pl-5">
            {m.open_recoveries.map((o) => (
              <li key={o.seat_key}>
                {o.title}: {o.status} at {o.step}
                {o.why ? ` (${o.why})` : ""}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section
        id="review-summary"
        title="Summary for the client"
        description="From the HTVA team. Share it yourself; it never names an internal agent."
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="review-summary-text">Client summary</Label>
          <Textarea
            id="review-summary-text"
            value={summary}
            rows={8}
            onChange={(e) => setSummary(e.target.value)}
          />
          <Label htmlFor="review-note-text">Your notes (never shown to the client)</Label>
          <Textarea
            id="review-note-text"
            value={note}
            rows={3}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={save.isPending}
              onClick={() => save.mutate(false)}
            >
              Save
            </Button>
            <Button
              disabled={save.isPending || r.status === "reviewed"}
              onClick={() => save.mutate(true)}
            >
              {r.status === "reviewed" ? "Reviewed" : "Mark reviewed"}
            </Button>
          </div>
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The review could not be saved.")}
            </Notice>
          ) : null}
        </div>
      </Section>
    </div>
  );
}
